import { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNotificationStore } from '@/stores/notification';
import { canUseMultiEstablishmentContext, useAuthStore } from '@/stores/auth';
import { useWebSocket } from '@/hooks/useWebSocket';
import { useAuthorizationStore } from '@/stores/authorization';
import { SystemPreferencesMenu } from '@/components/ui/system-preferences-menu';
import { NotificationDrawer } from '@/components/ui/notification-drawer';
import { RouteSearchDialog } from '@/components/ui/route-search-dialog';
import { EstablishmentContextSelect } from '@/components/context/EstablishmentContextSelect';
import { IoMdNotifications } from 'react-icons/io';
import LogoGeoScalaLight from '@/assets/images/logo-principal-light.svg';
import LogoGeoScalaBlack from '@/assets/images/logo-principal-dark.svg';
import { useTheme } from '@/hooks/useTheme';

export const Topbar = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { unreadCount, setDrawerOpen } = useNotificationStore();
  const {
    user,
    isSelectingContext,
    contextSelectionRequired,
    requireContextSelection,
    selectEstablishmentContext,
  } = useAuthStore();
  const selectedModuleKey = useAuthorizationStore((s) => s.selectedModuleKey);
  const [searchOpen, setSearchOpen] = useState(false);
  const defaultEstablishmentSelectionAttempted = useRef(false);

  useWebSocket({
    onConnected: () => {},
    onDisconnected: () => {},
  });

  const isMac =
    typeof navigator !== 'undefined' &&
    /Mac|iPhone|iPad|iPod/.test(navigator.platform);

  const handleOpenSearch = useCallback(() => setSearchOpen(true), []);
  const handleCloseSearch = useCallback(() => setSearchOpen(false), []);
  const establishments = user?.establishments ?? [];
  const selectedContextIds = user?.establishment_config_ids ?? [];
  const allEstablishmentIds = establishments.map(
    (item) => item.establishment_config_id,
  );
  const currentContextId =
    selectedContextIds.length > 1 &&
    selectedContextIds.length === allEstablishmentIds.length
      ? 'all'
      : (user?.establishment_config_id ?? '');
  const canSwitchContext =
    selectedModuleKey === 'medical_company' &&
    canUseMultiEstablishmentContext(user);
  const canUseOldEstablishmentSelector =
    selectedModuleKey === 'establishment' &&
    !contextSelectionRequired &&
    establishments.length > 1;
  const firstEstablishmentConfigId =
    establishments[0]?.establishment_config_id ?? '';
  const hasValidEstablishmentContext = establishments.some(
    (item) => item.establishment_config_id === user?.establishment_config_id,
  );
  const establishmentSelectorValue = hasValidEstablishmentContext
    ? (user?.establishment_config_id ?? '')
    : firstEstablishmentConfigId;
  const selectedEstablishmentNames = establishments
    .filter((item) => selectedContextIds.includes(item.establishment_config_id))
    .map((item) => item.name);
  const contextLabel =
    currentContextId === 'all'
      ? 'Todos'
      : selectedEstablishmentNames.length > 1
        ? `${selectedEstablishmentNames.length} estabelecimentos`
        : (selectedEstablishmentNames[0] ?? 'Selecionar contexto');

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const modifier = isMac ? e.metaKey : e.ctrlKey;
      if (modifier && e.key === 'k') {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isMac]);

  useEffect(() => {
    if (
      canUseOldEstablishmentSelector &&
      firstEstablishmentConfigId &&
      !contextSelectionRequired &&
      !hasValidEstablishmentContext &&
      !isSelectingContext &&
      !defaultEstablishmentSelectionAttempted.current
    ) {
      defaultEstablishmentSelectionAttempted.current = true;
      void selectEstablishmentContext(firstEstablishmentConfigId).catch(
        () => {},
      );
    }
  }, [
    canUseOldEstablishmentSelector,
    contextSelectionRequired,
    firstEstablishmentConfigId,
    hasValidEstablishmentContext,
    isSelectingContext,
    selectEstablishmentContext,
  ]);

  const handleSelectEstablishmentContext = (value: string) => {
    if (!value) return;
    void selectEstablishmentContext(value);
  };

  return (
    <>
      <motion.header
        className="relative flex h-16 items-center justify-between bg-background px-6"
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <motion.div
          className="absolute left-0 bottom-0 w-full h-[2px] bg-primary/60 dark:bg-[rgba(255,255,255,0.08)]"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.7, ease: 'easeInOut', delay: 0.2 }}
          style={{ transformOrigin: 'center' }}
        />
        <motion.div
          className="z-10 flex items-center space-x-3"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
        >
          <motion.img
            src={isDark ? LogoGeoScalaBlack : LogoGeoScalaLight}
            alt="Logo"
            className="h-12 w-36 cursor-pointer"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6 }}
            whileHover={{ scale: 1.01, rotate: 1.5 }}
            onClick={() => {
              window.location.href = '/';
            }}
          />
        </motion.div>

        <motion.div
          className="pointer-events-none absolute mr-32 inset-0 z-0 flex items-center justify-center"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
        >
          <motion.button
            className="pointer-events-auto flex h-7 w-52 justify-between gap-8 border border-border pl-4 text-sm text-card-foreground/50 transition-colors hover:bg-card-foreground/5"
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: 208 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            style={{
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              borderRadius: '0.3rem',
            }}
            onClick={handleOpenSearch}
          >
            <span className="flex items-center text-sm">
              {t('common.search', { defaultValue: 'Pesquisar' })}
            </span>
            <span className="flex items-center bg-card-foreground/10 px-4 text-sm font-medium text-card-foreground/50">
              {isMac ? '⌘ + K' : 'Ctrl + K'}
            </span>
          </motion.button>
        </motion.div>

        <motion.div
          className="z-10 flex items-center"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
        >
          {canUseOldEstablishmentSelector && (
            <div className="mr-3 w-64">
              <EstablishmentContextSelect
                value={establishmentSelectorValue}
                onChange={handleSelectEstablishmentContext}
                placeholder="Selecionar estabelecimento"
                disabled={isSelectingContext}
                variant="topbar"
                includeAllOption={false}
              />
            </div>
          )}

          {canSwitchContext && (
            <div className="mr-3 w-64">
              <button
                type="button"
                onClick={() => requireContextSelection()}
                className="flex h-[1.9rem] w-full items-center justify-between rounded-[0.3rem] border border-border bg-background px-3 text-left text-sm text-card-foreground/75 transition-colors hover:bg-card-foreground/5"
              >
                <span className="min-w-0 flex-1 truncate">{contextLabel}</span>
                <span className="ml-2 text-card-foreground/50">▾</span>
              </button>
            </div>
          )}

          <motion.button
            className="p-2 rounded-full hover:bg-accent transition-colors relative"
            whileHover={{ scale: 1.15 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setDrawerOpen(true)}
          >
            <IoMdNotifications className="w-7 h-7 text-foreground" />
            {unreadCount > 0 && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center"
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </motion.span>
            )}
          </motion.button>

          <SystemPreferencesMenu />
        </motion.div>
      </motion.header>

      <NotificationDrawer />
      <RouteSearchDialog open={searchOpen} onClose={handleCloseSearch} />
    </>
  );
};
